/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

import { createApi } from "@reduxjs/toolkit/query/react"

import { axiosBaseQuery } from "./apiClient"
import {
  apiBaseUrl,
  apiReducerPaths,
  dpApiMerchantUrl,
  mexCoreApiBaseUrl,
  portalBaseUrl,
  userProfileBaseUrl,
  CACHE_QUERY_TAGS,
  MERCHANT_GROUP_CACHE_TAGS,
} from "./constants"
import { serializeQueryArgsExcludingUiOptions } from "./serializeQueryArgs"

/**
 * Base API for Food Merchant endpoints (apiBaseUrl).
 * All domain slices targeting /food/merchant should inject into this API.
 */
export const foodMerchantBaseApi = createApi({
  reducerPath: apiReducerPaths.foodMerchantApi,
  baseQuery: axiosBaseQuery(apiBaseUrl),
  serializeQueryArgs: serializeQueryArgsExcludingUiOptions,
  refetchOnMountOrArgChange: 300,
  tagTypes: [
    CACHE_QUERY_TAGS.listSellingTimes,
    CACHE_QUERY_TAGS.searchMenusForMex,
    CACHE_QUERY_TAGS.martItemMenuDisplayV2,
    CACHE_QUERY_TAGS.menuV3Items,
    CACHE_QUERY_TAGS.menuV3Hierarchies,
    CACHE_QUERY_TAGS.menuV3ModifierGroups,
  ],
  endpoints: () => ({}),
})

/**
 * Base API for DelvPlatform API endpoints (dpApiMerchantUrl).
 * All domain slices targeting /delvplatformapi/merchant should inject into this API.
 */
export const dpApiBaseApi = createApi({
  reducerPath: apiReducerPaths.dpApi,
  baseQuery: axiosBaseQuery(dpApiMerchantUrl),
  serializeQueryArgs: serializeQueryArgsExcludingUiOptions,
  refetchOnMountOrArgChange: 300,
  tagTypes: [MERCHANT_GROUP_CACHE_TAGS.stores, CACHE_QUERY_TAGS.bulkTasks],
  endpoints: () => ({}),
})

/**
 * Base API for Portal (FoodTroy) endpoints (portalBaseUrl).
 * All domain slices targeting /foodtroy should inject into this API.
 *
 * No endpoints are injected yet. This base API is pre-registered so that
 * future domain slices can call portalBaseApi.injectEndpoints() without
 * needing to touch store.ts. Add the first endpoint before shipping to production.
 */
export const portalBaseApi = createApi({
  reducerPath: apiReducerPaths.portalApi,
  baseQuery: axiosBaseQuery(portalBaseUrl),
  serializeQueryArgs: serializeQueryArgsExcludingUiOptions,
  refetchOnMountOrArgChange: 300,
  endpoints: () => ({}),
})

/**
 * Base API for user-profile endpoints (userProfileBaseUrl).
 * All domain slices targeting /user-profile should inject into this API.
 */
export const userProfileBaseApi = createApi({
  reducerPath: apiReducerPaths.userProfileApi,
  baseQuery: axiosBaseQuery(userProfileBaseUrl),
  serializeQueryArgs: serializeQueryArgsExcludingUiOptions,
  refetchOnMountOrArgChange: 300,
  endpoints: () => ({}),
})

/**
 * Base API for Garnet / mex-app Troy gateway endpoints
 * (e.g. migration checklist + checklist status).
 */
export const mexCoreBaseApi = createApi({
  reducerPath: apiReducerPaths.mexCoreApi,
  baseQuery: axiosBaseQuery(mexCoreApiBaseUrl),
  serializeQueryArgs: serializeQueryArgsExcludingUiOptions,
  refetchOnMountOrArgChange: 300,
  endpoints: () => ({}),
})
